// ============================================================
// THE GM'S CUT - every display camera to one figure (plan map-display,
// Track C; ADR 2026-10-07: "cut to" is not a setting but a short-lived
// direction, and it overrides each viewer's options for that one move).
//
// Stored in `direction/cut`. The sequence only grows (it is the GM's clock),
// so a second cut to the same figure is a second move. A screen moves only
// for a FRESH cut, under ~30 s old: one a screen opened later finds waiting
// is old news (a reload a moment after the GM's cut still goes there).
// Freshness reads the clock rather than "the first snapshot", because a
// watch's first snapshot may be the cache's guess, the stored cut after it.
// A hidden figure is never cut to: the GM's hook refuses it.
//
// Pure. Zero React dependency.
// ============================================================

/** One cut: the figure, and a sequence that only grows. */
export interface CameraCut {
  id: string;
  seq: number;
}

/** The next cut to `id` after `last`: its sequence is `now`, or one past the last when the clock lags. */
export function cutTo(id: string, last: CameraCut | null, now: number): CameraCut {
  return { id, seq: Math.max(now, (last?.seq ?? 0) + 1) };
}

/** A stored cut, validated; anything else is no cut. */
export function readCut(raw: unknown): CameraCut | null {
  const c = raw as Partial<CameraCut> | null | undefined;
  if (!c || typeof c.id !== 'string' || !c.id || typeof c.seq !== 'number') return null;
  return { id: c.id, seq: c.seq };
}

/** How long a cut is news: a screen opened later than this does not move for it. Wide, for clocks that disagree. */
const FRESH_MS = 30_000;

/** Is this cut still news at `now` (this screen's clock)? */
export const isFresh = (cut: CameraCut, now: number) => Math.abs(now - cut.seq) < FRESH_MS;
